/**
 * Yup is the library we use for form validation
 *
 * getErrorMessage is the utility function to get error message from the standardized error codes.
 */
import * as Yup from 'yup';
import {isValidPhoneNumber, parsePhoneNumber} from 'libphonenumber-js';
import {getErrorMessage} from './getMessages';
import {
  emailRegex,
  nameRegex,
  internationalPhoneRegex,
  onlyAlphaWithParensRegex,
  onlyAlphaRegex,
  alphaNumericSpaceRegex,
  alphaNumericWithParensPeriodRegex,
  alphaNumericWithParensPeriodCommaRegex,
  hospitalNameRegex,
  specialistDesignationRegex,
  specialistEduccationRegex,
  licenseNumber,
  professionalExperienceAndQualificationRegex,
  awardsAndAchievementsRegex,
  areasOfExpertiseRegex,
} from '@/constants/regex';
import {maxImageSize} from '@/constants';

/**
 * Validates phone number with special handling for Indian mobile numbers
 * Indian mobile numbers must start with 6, 7, 8, or 9
 */
const validatePhoneNumber = (value: string | undefined, advance_validation = true): boolean => {
  if (!value) return false;

  try {
    // First check if it's a valid phone number format
    if (!isValidPhoneNumber(value)) {
      return false;
    }

    if (advance_validation) {
      // Parse the phone number to get country code and national number
      const phoneNumber = parsePhoneNumber(value);

      // Check if it's an Indian number (+91)
      if (phoneNumber?.countryCallingCode === '91') {
        const nationalNumber = phoneNumber.nationalNumber;

        // Indian mobile numbers must start with 6, 7, 8, or 9
        if (nationalNumber && nationalNumber.length > 0) {
          const firstDigit = nationalNumber[0];
          if (!['6', '7', '8', '9'].includes(firstDigit)) {
            return false;
          }
        }
      }
    }

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Sign up Form Validation Schema
 */
export const SignupFormValidation = Yup.object({
  // required and email validation
  email_id: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),

  // required, max 50 character and no invalid characters,
  name: Yup.string().required(getErrorMessage('E-10002')).max(50, getErrorMessage('E-10010')).matches(nameRegex, getErrorMessage('E-10005')),

  phone: Yup.string()
    .required(getErrorMessage('E-10025'))
    .test('is-valid-phone', getErrorMessage('E-10040'), value => {
      return validatePhoneNumber(value);
    }),
});

/**
 * Login up Form Validation Schema
 */
export const LoginFormValidation = Yup.object({
  // required and email validation
  email_id: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),
});

/**
 * OTP verification
 */
export const OTPValidation = Yup.object({
  otp: Yup.string().required(getErrorMessage('E-10008')).length(6, getErrorMessage('E-10024')),
});

/**
 * Specialist Signup Form Validation Schema
 */
export const SpecialistSignupValidation = Yup.object({
  // Step 1 - Basic Information
  name: Yup.string().required(getErrorMessage('E-10002')).max(50, getErrorMessage('E-10010')).matches(nameRegex, getErrorMessage('E-10005')),
  email_id: Yup.string().required(getErrorMessage('E-10039')).matches(emailRegex, getErrorMessage('E-10004')),
  phone: Yup.string()
    .required(getErrorMessage('E-10025'))
    .test('is-valid-phone', getErrorMessage('E-10040'), value => {
      return validatePhoneNumber(value);
    }),
  specialization: Yup.string().required(getErrorMessage('E-10028')),

  education: Yup.string()
    .trim()
    .required(getErrorMessage('E-10026'))
    .matches(specialistEduccationRegex, {
      message: getErrorMessage('E-10066'),
      excludeEmptyString: true,
    }),
  designation: Yup.string().required(getErrorMessage('E-10027')).matches(specialistDesignationRegex, getErrorMessage('E-10067')),
  experience: Yup.number().required(getErrorMessage('E-10029')).min(0, 'Experience cannot be negative').max(100, 'Experience cannot exceed 100 years'),
  license_number: Yup.string().trim().matches(licenseNumber, getErrorMessage('E-10212')).required(getErrorMessage('E-10200')),
  license_year: Yup.string()
    .trim()
    .required(getErrorMessage('E-10201'))
    .matches(/^\d+$/, getErrorMessage('E-10193'))
    .matches(/^\d{4}$/, getErrorMessage('E-10194'))
    .test('is-valid-year', getErrorMessage('E-10195'), value => {
      if (!value) return false;
      const year = parseInt(value, 10);
      const currentYear = new Date().getFullYear();
      return year >= 1900 && year <= currentYear;
    }),
  license: Yup.mixed().required(getErrorMessage('E-10030')),
});

export const EditProfileValidationSchema = Yup.object({
  name: Yup.string().required(getErrorMessage('E-10002')).matches(nameRegex, getErrorMessage('E-10005')).max(50, getErrorMessage('E-10010')),
  dob: Yup.date().nullable().max(new Date(), getErrorMessage('E-10070')),
  email: Yup.string().required(getErrorMessage('E-10039')).matches(emailRegex, getErrorMessage('E-10004')),
  phone: Yup.string()
    .required(getErrorMessage('E-10025'))
    .test('is-valid-phone', getErrorMessage('E-10040'), value => {
      return validatePhoneNumber(value);
    }),
  weight: Yup.number().nullable().min(1, getErrorMessage('E-10085')).max(500, getErrorMessage('E-10085')),
  height: Yup.number().nullable().min(30, getErrorMessage('E-10083')).max(300, getErrorMessage('E-10083')),
  gender: Yup.number().nullable(),
  address: Yup.string().nullable().min(10, getErrorMessage('E-10086')),
  medical_history: Yup.string().nullable(),
});

const areasOfExpertiseEntryValidation = Yup.string()
  .trim()
  .required(getErrorMessage('E-10111'))
  .max(250, getErrorMessage('E-10116'))
  .matches(areasOfExpertiseRegex, {
    message: getErrorMessage('E-10214'),
    excludeEmptyString: true,
  });

const awardsEntryValidation = Yup.string()
  .trim()
  .required(getErrorMessage('E-10111'))
  .max(250, getErrorMessage('E-10116'))
  .matches(awardsAndAchievementsRegex, {
    message: getErrorMessage('E-10213'),
    excludeEmptyString: true,
  });

export const SpecialistProfileEditValidationSchema = Yup.object({
  name: Yup.string().trim().required(getErrorMessage('E-10002')).matches(nameRegex, getErrorMessage('E-10005')).max(50, getErrorMessage('E-10010')),
  email: Yup.string().trim().required(getErrorMessage('E-10039')).matches(emailRegex, getErrorMessage('E-10004')),
  phone: Yup.string()
    .required(getErrorMessage('E-10025'))
    .test('is-valid-phone', getErrorMessage('E-10040'), value => {
      return validatePhoneNumber(value);
    }),
  location: Yup.number().required(getErrorMessage('E-10111')).typeError(getErrorMessage('E-10111')),
  designation: Yup.string()
    .trim()
    .required(getErrorMessage('E-10111'))
    .max(50, getErrorMessage('E-10112'))
    .matches(specialistDesignationRegex, getErrorMessage('E-10067')),
  education: Yup.string()
    .trim()
    .required(getErrorMessage('E-10111'))
    .matches(specialistEduccationRegex, {
      message: getErrorMessage('E-10066'),
      excludeEmptyString: true,
    }),
  specialization: Yup.number().required(getErrorMessage('E-10111')).typeError(getErrorMessage('E-10111')),
  license_number: Yup.string().nullable().matches(licenseNumber, getErrorMessage('E-10212')),
  license_issue_year: Yup.string()
    .nullable()
    .trim()
    .matches(/^\d{4}$/, getErrorMessage('E-10194'))
    .test('is-valid-year', getErrorMessage('E-10195'), value => {
      if (!value) return true; // Allow empty/null
      const year = parseInt(value, 10);
      const currentYear = new Date().getFullYear();
      return year >= 1900 && year <= currentYear;
    }),
  years_of_experience: Yup.number()
    .typeError(getErrorMessage('E-10111'))
    .required(getErrorMessage('E-10111'))
    .min(0, getErrorMessage('E-10113'))
    .max(60, getErrorMessage('E-10113')),
  professional_experience: Yup.string()
    .trim()
    .required(getErrorMessage('E-10111'))
    .max(600, getErrorMessage('E-10107'))
    .matches(professionalExperienceAndQualificationRegex, {
      message: getErrorMessage('E-10114'),
      excludeEmptyString: true,
    }),
  qualifications: Yup.string()
    .trim()
    .required(getErrorMessage('E-10111'))
    .max(600, getErrorMessage('E-10107'))
    .matches(professionalExperienceAndQualificationRegex, {
      message: getErrorMessage('E-10115'),
      excludeEmptyString: true,
    }),
  awards_achievements: Yup.array().of(awardsEntryValidation).nullable(),
  areas_of_expertise: Yup.array().of(areasOfExpertiseEntryValidation).nullable(),
  photo: Yup.mixed().required(getErrorMessage('E-10030')),
  license_file: Yup.mixed().required(getErrorMessage('E-10030')),
  signature: Yup.mixed()
    .required(getErrorMessage('E-10030'))
    .test('required', getErrorMessage('E-10030'), function (value) {
      // Explicit check for null/undefined/empty
      return value !== null && value !== undefined && value !== '';
    })
    .test('file-type', getErrorMessage('E-10023'), function (value) {
      if (!value) return false;
      if (typeof value === 'string') return true; // Existing file URL
      if (value instanceof File) {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        return validTypes.includes(value.type);
      }
      return false;
    })
    .test('file-size', getErrorMessage('E-10013'), function (value) {
      if (!value) return false;
      if (typeof value === 'string') return true; // Existing file URL
      if (value instanceof File) {
        const maxSize = 2 * 1024 * 1024; // 2MB
        return value.size <= maxSize;
      }
      return false;
    }),
});

export const ExternalRefferalValidationSchema = Yup.object({
  email: Yup.string().optional().matches(emailRegex, getErrorMessage('E-10004')),
  phone: Yup.string()
    .optional()
    .test('is-valid-phone', getErrorMessage('E-10040'), value => {
      // If empty or undefined, it's valid (because it's optional)
      if (!value || value.trim() === '') {
        return true;
      }

      // Only validate if there's actual content
      return validatePhoneNumber(value);
    }),
});

/**
 * Add New Admin Form Validation Schema
 */
export const AddAdminFormValidation = Yup.object({
  name: Yup.string().required(getErrorMessage('E-10002')).matches(nameRegex, getErrorMessage('E-10005')).max(50, getErrorMessage('E-10010')),
  email: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),
  phone: Yup.string()
    .required(getErrorMessage('E-10025'))
    .test('is-valid-phone', getErrorMessage('E-10021'), value => {
      return validatePhoneNumber(value);
    }),
  role: Yup.string().required(getErrorMessage('E-10111')),
  status: Yup.string().required(getErrorMessage('E-10111')),
});
// for the validation of email during latex code in patient settings
export const DataExportValidation = Yup.object({
  email: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),
  reason: Yup.string().optional(),
});
//for the validation of deletion of account
export const DataDeleteValidation = Yup.object({
  email: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),
  val: Yup.string()
    .required()
    .matches(/DELETE/),
});

export const AddHospitalPocFormValidation = Yup.object({
  name: Yup.string().required(getErrorMessage('E-10002')).matches(nameRegex, getErrorMessage('E-10005')).max(50, getErrorMessage('E-10010')),
  email: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),
  phone: Yup.string()
    .required(getErrorMessage('E-10025'))
    .test('is-valid-phone', getErrorMessage('E-10021'), value => {
      return validatePhoneNumber(value);
    }),
  hospital_name: Yup.string().required(getErrorMessage('E-10152')).max(150, getErrorMessage('E-10156')).matches(hospitalNameRegex, getErrorMessage('E-10153')),
  status: Yup.string().required(getErrorMessage('E-10111')),
});
/**
 * Contact Form Validation Schema (Help Screen)
 */
export const ContactFormValidation = Yup.object({
  contact_email: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),
  question: Yup.string().required(getErrorMessage('E-10183')),
});

export const HospitalProfileEditValidationSchema = Yup.object({
  image: Yup.mixed().required(),

  name: Yup.string().required(getErrorMessage('E-10152')).max(150, getErrorMessage('E-10156')).matches(hospitalNameRegex, getErrorMessage('E-10153')),

  email: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),

  phone: Yup.string()
    .required(getErrorMessage('E-10164'))
    .test('is-valid-phone', getErrorMessage('E-10165'), function (value) {
      // If empty or undefined, skip this test and let required validation handle it
      if (!value || value.trim() === '') {
        return true;
      }
      // If value exists, validate it
      const isValid = validatePhoneNumber(value, false);
      // Return false if invalid to show the test error message (E-10165)
      return isValid;
    }),

  consultation_fee: Yup.number().typeError(getErrorMessage('E-10158')).required(getErrorMessage('E-10157')).moreThan(0, getErrorMessage('E-10205')),

  address: Yup.string().required(getErrorMessage('E-10159')).min(10, getErrorMessage('E-10160')),

  city: Yup.string().required(getErrorMessage('E-10161')),
  state: Yup.string().required(getErrorMessage('E-10162')),
  country: Yup.number().required(getErrorMessage('E-10163')),

  about: Yup.string().required(getErrorMessage('E-10167')).min(50, getErrorMessage('E-10168')),

  accreditation: Yup.string().optional(),

  specializations: Yup.array().min(1, getErrorMessage('E-10169')),

  hospital_type: Yup.number().required(getErrorMessage('E-10166')),
});

export const InviteHospitalSpecialistFormValidation = Yup.object({
  name: Yup.string().required(getErrorMessage('E-10002')).matches(nameRegex, getErrorMessage('E-10005')).max(50, getErrorMessage('E-10010')),
  email: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),
  phone: Yup.string()
    .required(getErrorMessage('E-10025'))
    .test('is-valid-phone', getErrorMessage('E-10021'), value => {
      return validatePhoneNumber(value);
    }),
  specialization: Yup.number().required(getErrorMessage('E-10028')),
});

export const EditHospitalSpecialistFormValidation = Yup.object({
  name: Yup.string().required(getErrorMessage('E-10002')).matches(nameRegex, getErrorMessage('E-10005')).max(50, getErrorMessage('E-10010')),
  email: Yup.string().required(getErrorMessage('E-10003')).matches(emailRegex, getErrorMessage('E-10004')),
  phone: Yup.string()
    .required(getErrorMessage('E-10025'))
    .test('is-valid-phone', getErrorMessage('E-10021'), value => {
      return validatePhoneNumber(value);
    }),
  specialization: Yup.number().required(getErrorMessage('E-10028')),
});
